import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MapPin, ArrowLeft, ShoppingBag, MessageSquare, Loader2, Shield, FileCheck } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ReportDialog } from "@/components/ReportDialog";
import { MarketplaceDisclaimer } from "@/components/MarketplaceDisclaimer";
import { TrustBadge } from "@/components/TrustBadge";

export default function ListingDetail() {
  const { id } = useParams();
  const [listing, setListing] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [buying, setBuying] = useState(false);
  const [msg, setMsg] = useState("");
  const [msgOpen, setMsgOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const { user, isFirearmsPassActive, isAgeVerified } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [sellerTrust, setSellerTrust] = useState(null);

  useEffect(() => {
    api.get(`/listings/${id}`).then(({ data }) => setListing(data));
    api.get("/categories").then(({ data }) => setCategories(data.marketplace));
  }, [id]);

  useEffect(() => {
    if (!listing?.seller_id) return;
    api.get(`/users/${listing.seller_id}`).then(({ data }) => setSellerTrust(data.trust)).catch(() => {});
  }, [listing?.seller_id]);

  const listingCat = categories.find((c) => c.id === listing?.category);
  const isFirearm = !!listingCat?.firearm_class;
  const needsPass = isFirearm && !isFirearmsPassActive;
  const needsVerify = isFirearm && isFirearmsPassActive && !isAgeVerified;
  const gateBuy = needsPass || needsVerify;

  const buy = async () => {
    if (!user) return navigate("/login");
    if (needsPass) return navigate("/subscribe");
    if (needsVerify) return navigate(`/firearms/verify?next=${encodeURIComponent(window.location.pathname)}`);
    setBuying(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        listing_id: id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setBuying(false);
    }
  };

  const send = async () => {
    if (!user) return navigate("/login");
    if (!msg.trim()) return;
    setSending(true);
    try {
      await api.post("/messages", {
        recipient_id: listing.seller_id,
        body: msg,
        listing_id: listing.listing_id,
      });
      toast.success("Message sent");
      setMsg("");
      setMsgOpen(false);
      navigate("/messages");
    } catch (e) {
      toast.error("Couldn't send");
    } finally {
      setSending(false);
    }
  };

  const markSold = async () => {
    try {
      await api.post(`/listings/${id}/sold`);
      toast.success("Marked as sold");
      const { data } = await api.get(`/listings/${id}`);
      setListing(data);
    } catch (e) {
      toast.error("Failed");
    }
  };

  if (!listing) return <div className="max-w-4xl mx-auto px-4 py-16 flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;

  const isOwner = user?.user_id === listing.seller_id;
  const images = listing.image_paths || [];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-fade-in" data-testid="listing-detail">
      <button onClick={() => navigate(-1)} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-6" data-testid="btn-back">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <div className="rounded-md overflow-hidden bg-muted aspect-square relative">
            {images[activeImg] ? (
              <img src={fileUrl(images[activeImg])} alt={listing.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-muted-foreground">No photo</div>
            )}
            {listing.status === "sold" && (
              <div className="absolute top-4 right-4">
                <Badge className="bg-foreground text-background text-sm py-1 px-3">SOLD</Badge>
              </div>
            )}
          </div>
          {images.length > 1 && (
            <div className="grid grid-cols-5 gap-2 mt-3">
              {images.map((p, i) => (
                <button key={p} onClick={() => setActiveImg(i)} className={`rounded-md overflow-hidden aspect-square border-2 ${activeImg === i ? "border-primary" : "border-transparent"}`} data-testid={`thumb-${i}`}>
                  <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="border-accent/40 text-accent bg-accent/5">
              {listingCat?.name || listing.category}
            </Badge>
            {isFirearm && (
              <Badge className="bg-secondary text-secondary-foreground inline-flex items-center gap-1" data-testid="badge-firearm">
                <Shield className="w-3 h-3" /> {listingCat?.min_age}+ · Firearms Pass
              </Badge>
            )}
            {listing.requires_ffl && (
              <Badge variant="outline" className="border-secondary/60 text-secondary bg-secondary/5 inline-flex items-center gap-1" data-testid="badge-ffl">
                <FileCheck className="w-3 h-3" /> FFL transfer required
              </Badge>
            )}
          </div>
          <h1 className="font-display font-extrabold text-3xl md:text-4xl mt-3 leading-tight">{listing.title}</h1>
          <div className="mt-3 font-display font-extrabold text-4xl text-secondary" data-testid="listing-price">${listing.price.toFixed(2)}</div>

          <div className="flex items-center gap-4 mt-4 text-sm text-muted-foreground">
            <Badge variant="outline">{listing.condition}</Badge>
            <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" /> {listing.location}</span>
            <span>· {formatDistanceToNow(new Date(listing.created_at), { addSuffix: true })}</span>
          </div>

          <p className="mt-6 whitespace-pre-wrap text-base leading-relaxed">{listing.description}</p>

          {isFirearm && listing.ffl_notes && (
            <div className="mt-4 bg-secondary/5 border-l-4 border-secondary rounded-r-md p-4">
              <div className="text-xs uppercase tracking-widest text-secondary font-semibold">FFL notes from seller</div>
              <p className="text-sm mt-1 leading-relaxed whitespace-pre-wrap">{listing.ffl_notes}</p>
            </div>
          )}

          {isFirearm && (
            <div className="mt-6">
              <MarketplaceDisclaimer compact />
            </div>
          )}

          <Card className="mt-8 bg-muted/40 border-border">
            <CardContent className="p-4 flex items-center gap-3 flex-wrap">
              <Link to={`/profile/${listing.seller_id}`} className="flex items-center gap-3 flex-1 min-w-[200px]" data-testid="link-seller">
                <Avatar className="w-11 h-11"><AvatarImage src={listing.seller_picture} /><AvatarFallback>{listing.seller_name?.[0]}</AvatarFallback></Avatar>
                <div>
                  <div className="font-semibold">{listing.seller_name}</div>
                  <div className="text-xs text-muted-foreground">Seller</div>
                </div>
              </Link>
              {sellerTrust && <TrustBadge trust={sellerTrust} compact />}
            </CardContent>
          </Card>

          <div className="mt-6 flex flex-wrap gap-3">
            {isOwner ? (
              listing.status !== "sold" && (
                <Button onClick={markSold} variant="outline" data-testid="btn-mark-sold">Mark as sold</Button>
              )
            ) : (
              <>
                {listing.status === "available" && (
                  <Button onClick={buy} disabled={buying} size="lg" className="bg-primary hover:bg-primary/90 h-12" data-testid="btn-buy">
                    {buying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : gateBuy ? <Shield className="w-4 h-4 mr-2" /> : <ShoppingBag className="w-4 h-4 mr-2" />}
                    {gateBuy ? (needsPass ? "Firearms Pass required" : "Verify age to buy") : `Buy now · $${listing.price.toFixed(2)}`}
                  </Button>
                )}
                <Dialog open={msgOpen} onOpenChange={setMsgOpen}>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="lg"
                      className="h-12"
                      data-testid="btn-open-message"
                      onClick={(e) => {
                        if (gateBuy) {
                          e.preventDefault();
                          if (!user) return navigate("/login");
                          if (needsPass) return navigate("/subscribe");
                          return navigate(`/firearms/verify?next=${encodeURIComponent(window.location.pathname)}`);
                        }
                      }}
                    >
                      {isFirearm && gateBuy ? <Shield className="w-4 h-4 mr-2" /> : <MessageSquare className="w-4 h-4 mr-2" />}
                      {isFirearm && gateBuy
                        ? (needsPass ? "Pass required to contact" : "Verify age to contact")
                        : "Contact seller"}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-card">
                    <DialogHeader>
                      <DialogTitle>Message {listing.seller_name}</DialogTitle>
                    </DialogHeader>
                    <Textarea
                      value={msg}
                      onChange={(e) => setMsg(e.target.value)}
                      placeholder="Hey, still available?"
                      className="min-h-[120px]"
                      data-testid="input-message-seller"
                    />
                    <DialogFooter>
                      <Button onClick={send} disabled={sending || !msg.trim()} className="bg-primary hover:bg-primary/90" data-testid="btn-send-message">
                        {sending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Send
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </>
            )}
          </div>
          {!isOwner && (
            <div className="mt-4">
              <ReportDialog targetType="listing" targetId={listing.listing_id} testId="btn-report-listing" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
